import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  useContext,
  useRoute,
  useRouter,
  useStore,
  watch,
} from '@nuxtjs/composition-api';
import { CancelTokenSource } from 'axios';
import { State } from '~/store';
import debounce from '~/utils/debounce';
import { Locale } from '~/types/locale';
import { defaultThumbnailOnError } from '~/utils/image';

interface AutocompleteCategory {
  old_id: number;
  name: string;
  link: string;
  score: number;
}

interface AutocompleteResource {
  old_id: number;
  name: string;
  link: string;
  score: number;
  type: string;
  thumbnail: string;
  country: Locale;
}

interface Props {
  overlay: boolean;
  id: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'SearchBar',
  props: {
    overlay: { type: Boolean },
    id: null
  },
  setup(__props: any) {



const route = useRoute();
const router = useRouter();
const store = useStore<State>();
const { i18n, $axios, localePath, $mzAnalyticsApi } = useContext();
const savedSearchTerms = computed<string[]>(() => store.getters.savedSearchTerms);

const query = ref<string>('');
const searchInput = ref<HTMLInputElement | null>(null);
const categories = ref<AutocompleteCategory[]>([]);
const resources = ref<AutocompleteResource[]>([]);
const isSearching = ref(false);
const resultsLoading = ref(false);
const size = ref(0);
const source = ref<CancelTokenSource | null>(null);

const searchForm = ref<HTMLFormElement | null>(null);

watch(route, (newRoute, oldRoute) => {
  if (newRoute?.params.keyword !== oldRoute?.params.keyword) {
    query.value = newRoute?.params.keyword;
  }
});

onMounted(() => {
  if (route.value?.params && 'keyword' in route.value?.params) {
    query.value = route.value.params.keyword;
  }
  store.dispatch('getSavedSearchTerms');

  window.addEventListener('click', onWindowBlur, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('click', onWindowBlur);
});

const cancelRequest = () => {
  if (source.value) {
    source.value.cancel('More recent API request');
  }
};

const highlight = (str: string) => {
  if (!str) {
    return;
  }
  if (!query.value) {
    return;
  }
  // Yellow highlight the matching part of the code code
  return str.replace(
    new RegExp(query.value.replace(/[#]/gi, ''), 'gi'),
    match => '<mark>' + match + '</mark>'
  );
};

const onWindowBlur = (event: Event) => {
  if (!isSearching.value) {
    return;
  }
  if (!searchForm.value || !searchForm.value.contains(event.target as HTMLElement)) {
    closeAutocomplete();
  }
};

const closeAutocomplete = () => {
  query.value = '';
  toggleIsSearching();
  if (document.activeElement && document.activeElement instanceof HTMLElement) {
    document.activeElement?.blur();
  }
  cancelRequest();
};

const onSearchInput = debounce((abort = false) => {
  // Start searching after 2 characters
  if (
    abort ||
    (query.value.replace(/[#]/gi, '').length > 0 && query.value.replace(/[#]/gi, '').length < 3)
  ) {
    return;
  }

  // Debounce query by 300ms when typing
  toggleIsSearching(true);
  search();
}, 250);

const search = async () => {
  cancelRequest();
  source.value = $axios.CancelToken.source();
  resultsLoading.value = true;
  size.value = 0;

  store.dispatch('loadingSearchResults', true);

  try {
    // Query Mongo
    const response = await $axios.$get('/public/v1/search/autocomplete', {
      params: {
        query: query.value.replace(/[#]/gi, '') || '',
        country: i18n.locale,
      },
      cancelToken: source.value.token,
    });

    categories.value = response.categories;
    resources.value = response.resources;
    size.value = response.resources.length;
  } catch (error) {
    if ($axios.isCancel(error)) {
      return Promise.reject(error);
    }

    store.dispatch('loadingSearchResults', false);
  } finally {
    resultsLoading.value = false;
  }
};

const submitSearch = async () => {
  // Abort the autocomplete
  onSearchInput(true);

  await saveQuery('Typed', undefined, undefined, undefined, true);
  router.push({
    path: localePath(
      `/search/${query.value.replace(/[#]/gi, '') ? `${query.value.replace(/[#]/gi, '')}/` : ''}`
    ),
  });

  const entityType = query.value.replace(/[#]/gi, '');

  $mzAnalyticsApi({
    event: 'click',
    name: 'search',
    productArea: 'search',
    entityType,
  });

  clearSavedSearch();
  closeAutocomplete();
  toggleIsSearching();
};

const saveQuery = async (
  type: 'Typed' | 'Autosuggest' | 'Saved' = 'Typed',
  searchName?: string,
  searchID?: number,
  searchType?: string,
  fromSubmitSearch?: boolean
) => {
  await eventSearchSubmitted(type, searchName, searchID, searchType, fromSubmitSearch);
  if (!query.value.replace(/[#]/gi, '') || query.value.replace(/[#]/gi, '') === '') {
    return;
  }

  // Remove duplicate terms from list
  const searchTerms =
    savedSearchTerms.value.filter(item => item !== query.value.replace(/[#]/gi, '')) || [];

  // Only store up to 6 recent terms
  if (searchTerms.length > 5) {
    searchTerms.splice(-1, 1);
  }

  searchTerms.unshift(query.value.replace(/[#]/gi, ''));
  store.dispatch('updateSavedSearchTerms', searchTerms);
};

const toggleIsSearching = (_isSearching = false) => {
  isSearching.value = _isSearching;
  if (!isSearching) {
    query.value = '';
    cancelRequest();
  }
  if (
    isSearching &&
    !query.value &&
    (resources.value.length === 0 ||
      (resources.value.length > 0 && resources.value[0].country !== i18n.locale))
  ) {
    search();
  }
};
const sendSearchFocusEvent = () => {
  // $mzAnalyticsApi({
  //   event: 'click',
  //   name: 'search.focused',
  //   productArea: 'search',
  // });
};

const clearSavedSearch = () => {
  store.dispatch('updateSavedSearchTerms', []);
};

const eventSearchSubmitted = (
  type: 'Autosuggest' | 'Typed' | 'Saved',
  searchName?: string,
  searchID?: number,
  searchType?: string,
  fromSubmitSearch?: boolean
) => {
  // this doesn't appear to do anything ? none of the values seem to be used
  if (type === 'Autosuggest' || type === 'Typed') {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const category = type === 'Autosuggest' ? 'Search Autocomplete' : 'Search';
    const gaEventType = searchType ? 'Resource' : 'Topic';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const autoSuggestType = type === 'Typed' ? 'Submit' : gaEventType;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const gaEventLabel =
      type === 'Typed'
        ? query.value.replace(/[#]/gi, '')
        : query.value.replace(/[#]/gi, '') + ' | ' + searchName + '( ' + searchID + ' )';
  }
  // we await this so it's sent before the route change
  if (!fromSubmitSearch) {
    $mzAnalyticsApi({
      event: 'click',
      name: 'search',
      productArea: 'search',
      entityType: query.value.replace(/[#]/gi, ''),
    });
  }
};

return { __sfc: true,route, router, store, i18n, $axios, localePath, $mzAnalyticsApi, savedSearchTerms, query, searchInput, categories, resources, isSearching, resultsLoading, size, source, searchForm, cancelRequest, highlight, onWindowBlur, closeAutocomplete, onSearchInput, search, submitSearch, saveQuery, toggleIsSearching, sendSearchFocusEvent, clearSavedSearch, eventSearchSubmitted, defaultThumbnailOnError }
}

})